import React, {Component} from 'react';
import PropTypes from "prop-types";

export default class Button extends Component {
    static propTypes = {
        /**
         *  add icon to button, you can add 2 icons (prepend and append)
         */
        addon: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.object,
        ]),
        /**
         * pass children as prop (button label)
         */
        label: PropTypes.string,
        /**
         * set button style to rounded
         */
        rounded: PropTypes.bool,
        /**
         * set button style (danger/primary..)
         */
        variant: PropTypes.string,
        /**
         * button link ("button" tag replaces to "a" tag)
         */
        link: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.bool,
        ]),
        /**
         * button name attribute
         */
        name: PropTypes.string,
        /**
         * button disabled attribute
         */
        disabled: PropTypes.bool,
        /**
         *  set button style to bold
         */
        bold: PropTypes.bool,
        /**
         * button text body on mobile devices
         */
        mobileLabel: PropTypes.string,
        /**
         * set class to in button text wrapper
         */
        spanClass: PropTypes.string,
        /**
         * button type attribute
         */
        type: PropTypes.string,
        /**
         * set wide padding for button (only x-axis)
         */
        wide: PropTypes.bool,
        /**
         * style object (react styles)
         */
        additionalStyle: PropTypes.object,
        /**
         * additional class name for button
         */
        additionalClass: PropTypes.string,
        /**
         * add input field to wrapper, on button click triggers input (can be submit/button/reset type), there is no <button> tag in wrapper, button wrapped in <div>
         */
        isInput: PropTypes.bool,
        /**
         * input value attribute
         */
        inputValue: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        /**
         * on click callback
         */
        onClick: PropTypes.func,
        /**
         * button size (xs/sm/md/lg)
         */
        size: PropTypes.oneOf(['xs','sm','md','lg']),
        /**
         * Component inner (replaces label)
         */
        children: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.node),
            PropTypes.node
        ])
    };

    constructor(props) {
        super(props);

        this.state = {
            label: props.label
        };

        this.getClasses = this.getClasses.bind(this);
        this.getAttributes = this.getAttributes.bind(this);
        this.setLabel = this.setLabel.bind(this);
        this.getAddon = this.getAddon.bind(this);
        this.getButtonTag = this.getButtonTag.bind(this);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.label !== this.props.label) {
            this.setState({label: this.props.label});
        }
    }

    setLabel(str) {
        this.setState({
            label: str
        })
    }

    getClasses() {
        const {
            rounded,
            style,
            variant,
            size,
            bold,
            additionalClass,
            wide,
            disableHeight,
            disabled,
            className
        } = this.props;

        const classes = [];

        if(style && typeof style === 'string') {
            classes.push(`btn-${style}`);
        }
        if(variant && typeof style !== 'string') {
            classes.push(`btn-${variant}`);
        }
        if(bold) {
            classes.push(`btn-bold`);
        }
        if(rounded) {
            classes.push(`btn-rounded`);
        }
        if(size && (size === 'md' || size === 'sm' || size === 'xs' || size === 'lg')) {
            classes.push(`btn-${size}`);
        }
        if(additionalClass) {
            classes.push(additionalClass);
        }
        if(wide) {
            classes.push('btn-wide');
        }
        if(disableHeight) {
            classes.push('btn-height-unset');
        }
        if(disabled) {
            classes.push('btn-disabled');
        }
        if(className) {
            classes.push(className);
        }

        return classes.join(' ');
    }

    getAttributes() {
        const {
            link,
            type,
            name ,
            disabled,
            additionalStyle,
            style,
            isInput,
            onClick,
        } = this.props;

        return {
            type: type && !isInput ? type : null,
            name: name && !isInput ? name : null,
            disabled: disabled && !isInput ? 'disabled' : null,
            onClick: onClick && !isInput ? onClick : null,
            href: link && !isInput ? link : null,
            style: style && typeof style === 'object' ? style : additionalStyle ? additionalStyle : null
        };
    }

    getAddon(addon, position) {
        const buttons = [];
        const addons = Array.isArray(addon) ? addon : [addon];

        for (const [key, item] of Object.entries(addons)) {
            if (item.position === position || (typeof item.position === 'undefined' && position === 'prepend')) {
                buttons.push(<i className={item.class} key={key}/>)
            }
        }

        return buttons;
    }

    getButtonTag() {
        const {isInput, link} = this.props;

        if(isInput) {
            return 'div';
        }

        return link ? 'a' : 'button';

    }

    render() {
        const reservedOptionNames = ['rounded','style','variant','bold','additionalClass', 'wide', 'disableHeight', 'additionalStyle','size','link', 'className'];

        const {
            addon,
            type,
            name,
            disabled,
            spanClass,
            mobileLabel,
            isInput,
            inputValue,
            onClick,
            children,
            ...rest
        } = this.props;

        const {label} = this.state;

        const fAddon = addon ? this.getAddon(addon, 'prepend') : null,
              lAddon = addon ? this.getAddon(addon, 'append') : null;

        const classes = this.getClasses();

        const attrs = this.getAttributes();

        const BtnTag = this.getButtonTag();

        const restOptions = {};

        for (const [key, value] of Object.entries(rest)) {
            if(!reservedOptionNames.includes(key)) {
                restOptions[key] = value;
            }
        }

        return (
            <BtnTag
                className={`btn ${classes}`}
                {...attrs}
                {...restOptions}
            >
                <span tabIndex="-1">
                    {fAddon}
                        <span className={`${spanClass ? spanClass : ''}${mobileLabel ? ' d-none d-lg-inline-flex': ''}`}>
                            {label || children}
                        </span>
                        {mobileLabel ? <span className="d-inline-flex d-lg-none" dangerouslySetInnerHTML={{__html: mobileLabel}}/> : ''}
                    {lAddon}
                </span>
                {isInput ? <input name={name} onClick={onClick} disabled={disabled} type={type || 'button'} value={inputValue} /> : ''}
            </BtnTag>
        )
    }
}
